import { useQuery } from '@apollo/client'
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
} from '@mui/material'
import { MoreVerticalIcon, PlusIcon } from 'lucide-react'
import { useState } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_TENANT_GROUPS,
  GetTenantGroupsQuery,
  GetTenantGroupsQueryVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

import DeliciousTable, { HeadCell } from '../components/DeliciousTable'
import { CreateRoleDialog } from '../forms/permissions/CreateRoleDialog'
import { DeleteRoleDialog } from '../forms/permissions/DeleteRoleDialog'
import { EditGroupPermissionsDialog } from '../forms/permissions/EditGroupPermissionsDialog'

type Group = GetTenantGroupsQuery['tenantGroups']['results'][0]

function ManageRoles() {
  const { t } = useT()
  const [editGroup, setEditGroup] = useState<Group | null>(null)
  const [createRoleOpen, setCreateRoleOpen] = useState(false)
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  )
  const [rowMenuGroup, setRowMenuGroup] = useState<Group | null>(null)
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null)

  const groupsData = useQuery<
    GetTenantGroupsQuery,
    GetTenantGroupsQueryVariables
  >(GET_TENANT_GROUPS)
  useRefresh(groupsData.refetch)

  const headCells: HeadCell[] = [
    {
      id: 'name',
      alignment: 'left',
      label: t('manageRoles.columns.name', 'Name'),
    },
    {
      id: 'permissions',
      alignment: 'left',
      label: t('manageRoles.columns.permissions', '# Permissions'),
    },
    {
      id: 'actions',
      alignment: 'right',
      label: t('manageRoles.columns.actions', 'Actions'),
    },
  ]

  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null)
    setRowMenuGroup(null)
  }

  const renderRow = (group: Group, index: number) => (
    <TableRow hover key={`${group.id}-${index}`}>
      <TableCell align="left">
        <Box ml={3}>{group.name}</Box>
      </TableCell>
      <TableCell align="left">{group.permissions.length}</TableCell>
      <TableCell align="right">
        <IconButton
          aria-label={t('manageRoles.rowActions', 'Role actions')}
          onClick={e => {
            setRowMenuAnchorEl(e.currentTarget)
            setRowMenuGroup(group)
          }}
          size="large"
        >
          <MoreVerticalIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  )

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 3, pt: 6 }}
    >
      <DeliciousTable<Group, unknown>
        title={t('manageRoles.title', 'Permissions')}
        size="small"
        data={groupsData.data?.tenantGroups.results || []}
        headCells={headCells}
        renderRow={renderRow}
        loading={groupsData.loading}
        error={groupsData.error?.message}
        RightHeaderComponent={
          <Button
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => setCreateRoleOpen(true)}
          >
            {t('manageRoles.actions.createRole', 'Create Role')}
          </Button>
        }
      />
      <EditGroupPermissionsDialog group={editGroup} setGroup={setEditGroup} />
      <CreateRoleDialog
        open={createRoleOpen}
        onClose={() => setCreateRoleOpen(false)}
      />
      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={handleRowMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (rowMenuGroup) {
              setEditGroup(rowMenuGroup)
            }
            handleRowMenuClose()
          }}
        >
          {t('common.edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenuGroup) {
              setGroupToDelete(rowMenuGroup)
            }
            handleRowMenuClose()
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </Menu>
      {groupToDelete && (
        <DeleteRoleDialog
          open
          onClose={() => setGroupToDelete(null)}
          groupId={groupToDelete.id}
          groupName={groupToDelete.name}
          onSuccess={() => setGroupToDelete(null)}
        />
      )}
    </Box>
  )
}

export default ManageRoles
